import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { api, errCode, errList, errMsg } from "@/lib/api";
import { MeasureTool } from "@/widget/MeasureTool";
import { DetailsForm } from "./FieldQuoteDetails";
import { ReviewPanel } from "./FieldQuoteReview";
import { stageOf } from "./Leads";
import { resetScroll } from "@/lib/mobileView";
import { trackOrg } from "@/lib/track";

const hex = (n: number) => Array.from(crypto.getRandomValues(new Uint8Array(n)), (b) => b.toString(16).padStart(2, "0")).join("");
const uuid4 = () => { const h = hex(16); return `${h.slice(0, 8)}-${h.slice(8, 12)}-4${h.slice(13, 16)}-a${h.slice(17, 20)}-${h.slice(20, 32)}`; };
const round = (c: any): any => (typeof c[0] === "number" ? c.map((n: number) => Math.round(n * 1e7) / 1e7) : c.map(round));
const toFeatures = (ms: any[]) => ms.map((m) => ({ type: "Feature", id: uuid4(), geometry: { type: m.geojson.type, coordinates: round(m.geojson.coordinates) },
  properties: { mode: m.geojson.type === "LineString" ? "linestring" : "polygon", kind: m.type, label: m.label } }));

export default function FieldQuoteEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const key = useRef(hex(16));
  const [cfg, setCfg] = useState<any>(null);
  const [d, setD] = useState<any>(null);
  const [step, setStep] = useState<"details" | "measure" | "review">("details");
  const [busy, setBusy] = useState(false);
  const [shapes, setShapes] = useState<any[]>([]);
  const [view, setView] = useState<any>(null);
  const [geoErrors, setGeoErrors] = useState<string[]>([]);
  const [missing, setMissing] = useState(false);
  const top = useRef<HTMLDivElement>(null);
  const firstStep = useRef(true);
  useEffect(() => {
    if (firstStep.current) { firstStep.current = false; return; }
    resetScroll(top.current);
  }, [step]);
  const [measureGate, setMeasureGate] = useState<{ ok: boolean | null; msg?: string }>({ ok: null });
  useEffect(() => {
    if (step !== "measure" || !id) return;
    setMeasureGate({ ok: null });
    api.post(`/orgs/me/field-quotes/${id}/measure-start`).then(() => setMeasureGate({ ok: true }))
      .catch((e) => setMeasureGate({ ok: false, msg: errMsg(e) }));
  }, [step, id]);

  const load = async (lid: string, pick = false) => {
    try {
      const { data } = await api.get(`/orgs/me/leads/${lid}`);
      if (data.lead.lead_source !== "field_quote") { setMissing(true); return; }
      setD(data);
      if (pick) { setShapes(toFeatures(data.measurements || [])); setStep(data.quote ? "review" : "measure"); }
    } catch (_) { setMissing(true); }
  };
  useEffect(() => { api.get("/orgs/me/field-quotes/config").then((r) => setCfg(r.data)); }, []);
  useEffect(() => { if (id) load(id, true); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveDetails = async (body: any) => {
    setBusy(true);
    try {
      if (id) {
        const { data } = await api.put(`/orgs/me/field-quotes/${id}`, body);
        if (data.estimate_reset) { setShapes([]); setView(null); toast.message("Service or address changed — please re-measure."); }
        await load(id); setStep(!data.estimate_reset && d?.quote ? "review" : "measure");
      } else {
        const { data } = await api.post("/orgs/me/field-quotes", { ...body, idempotency_key: key.current });
        nav(`/app/field-quotes/${data.id}`, { replace: true });
      }
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const estimate = async (store: any[], features: any[]) => {
    setShapes(store); setBusy(true); setGeoErrors([]);
    try { await api.post(`/orgs/me/field-quotes/${id}/estimate`, { features }); await load(id!); setStep("review"); }
    catch (e) { if (errCode(e) === "INVALID_GEOMETRY") setGeoErrors(errList(e)); else toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  if (missing) return <p data-testid="fq-not-found">Field quote not found. <Link to="/app/field-quotes" className="underline">Back</Link></p>;
  if (!cfg || (id && !d)) return <p className="text-muted-foreground">Loading…</p>;
  const lead = d?.lead;
  const svc = cfg.services.find((s: any) => s.id === lead?.selected_service_id);
  const initial = lead ? { name: lead.name, phone: lead.phone, email: lead.email, service_id: lead.selected_service_id,
    address: { formatted: lead.confirmed_service_address, place_id: lead.place_id, postal_code: lead.postal_code, ...lead.anchor } } : null;
  return (
    <div ref={top} className="fade-up max-w-xl mx-auto space-y-5" data-testid="field-quote-editor">
      <Link to="/app/field-quotes" className="text-sm text-muted-foreground inline-flex items-center gap-1 hover:underline"><ArrowLeft className="h-4 w-4" />Field Quotes</Link>
      <div>
        <h1 className="text-2xl font-bold" data-testid="fq-title">{lead ? lead.name : "New Field Quote"}</h1>
        {lead && <span className={`inline-block mt-1 text-xs rounded-full px-2.5 py-1 ${stageOf(lead).cls}`} data-testid="fq-stage-badge">{stageOf(lead).label}</span>}
        <ol className="flex gap-2 mt-3 text-xs" aria-label="Steps">
          {(["details", "measure", "review"] as const).map((s, i) => (
            <li key={s} className={`flex-1 h-1.5 rounded-full ${s === step ? "bg-primary" : i < ["details", "measure", "review"].indexOf(step) ? "bg-primary/40" : "bg-secondary"}`} />
          ))}
        </ol>
      </div>
      {step === "details" && <DetailsForm key={lead?.id || "new"} cfg={cfg} initial={initial} busy={busy} onSave={saveDetails} />}
      {step === "measure" && lead && measureGate.ok === false && <p className="text-sm text-destructive" role="alert" data-testid="fq-measure-blocked">{measureGate.msg}</p>}
      {step === "measure" && lead && measureGate.ok && (svc ? (
        <MeasureTool cfg={cfg} unit={svc.pricing_unit} address={lead.anchor} initial={shapes} view={view} onView={setView} busy={busy} onMapLoad={() => trackOrg("field_map_load", lead.id)}
          errors={geoErrors} onEdit={() => setGeoErrors((e) => (e.length ? [] : e))} onFail={() => toast.error("Satellite map unavailable right now.")} onSubmit={estimate} />
      ) : <p className="text-sm text-destructive">This service is no longer available. Edit details to pick another.</p>)}
      {step === "review" && d?.quote && <ReviewPanel d={d} onReload={() => load(id!)} onRemeasure={() => setStep("measure")} onEditDetails={() => setStep("details")} />}
      {step === "measure" && (
        <div className="flex gap-4">
          {d?.quote && <button className="text-sm underline text-muted-foreground min-h-[44px]" onClick={() => setStep("review")} data-testid="fq-back-to-review">Back to review (keep current estimate)</button>}
          <button className="text-sm underline text-muted-foreground min-h-[44px]" onClick={() => setStep("details")} data-testid="fq-back-to-details">Back to details</button>
        </div>
      )}
    </div>
  );
}
